/** The control tree a participant canvas draws: a pure function of the documents and the store.
 *
 * The render half builds absolute boxes, because a broadcast canvas is a fixed pixel size and a
 * node that moves is a node that moved on air. This half builds a tree that **flows**, and the
 * difference is not cosmetic: on a per-person canvas the cloud has already removed nodes this
 * person may not see, and a hole left where one of them sat tells this person what another person
 * may do. A flowed tree collapses; an absolute one leaks.
 */

import type { Entity } from "@nebulize/sdk";

import type { BoundValues } from "../render/scene/bindings";
import { formatText, predicatesHold, resolveBindings } from "../render/scene/bindings";
import type { SceneReport } from "../render/scene/report";
import { sceneReport } from "../render/scene/report";
// The render half's own matcher, shared rather than copied: both halves expand the same `repeat`
// over the same entities and have to answer identically, or one draws a row the other does not.
import { matchingEntities } from "../render/scene/sceneTree";
import type { EntityIndex } from "../render/transport/snapshotStore";
import { entityKey } from "../render/transport/snapshotStore";
import type { PressTime } from "./press";
import type {
  ControlKind,
  GateMode,
  SceneDocument,
  SceneNode,
} from "./contract";
import {
  BAR_VALUE_KEY,
  GATE_MODE_HIDE,
  INPUT_KIND,
  isControlKind,
  SUPPORTED_SCHEMA_VERSION,
} from "./contract";

const DEFAULT_FLOW_DIRECTION = "column";
const DEFAULT_FLOW_GAP = 0;
const DEFAULT_FLOW_MAX_ITEMS = 16;

/** What a control submits: a submission the manifest already declared, and one value. */
export interface ControlAction {
  readonly submission: string;
  readonly value: string;
}

/** What one press sends: the declared submission, its value, and exactly one press time. */
export interface PressAction extends ControlAction, PressTime {}

/** A node's gate as this half reads it: armed, locked, and what losing the permit would do. */
export interface ControlGate {
  readonly locked: boolean;
  /** The host's own words. The platform ships none of its own, so this may be empty. */
  readonly label: string;
  readonly mode: GateMode | undefined;
}

/** How a node arranges its children, when it arranges them at all. */
export interface ControlFlow {
  readonly direction: "row" | "column";
  readonly gap: number;
}

export interface ControlNode {
  /** Unique across documents, and stable across a resort because it carries the item's id. */
  readonly key: string;
  readonly id: string;
  readonly kind: ControlKind;
  /** The authored string after substitution: what a `text` draws and what a control is labelled. */
  readonly text: string;
  /** The accessible name this kind owes, never empty. */
  readonly name: string;
  /** A `bar` magnitude in 0..1, undefined for every other kind. */
  readonly fill: number | undefined;
  readonly flow: ControlFlow | undefined;
  /** What pressing this does, or undefined for a node nothing presses. */
  readonly action: ControlAction | undefined;
  /** The physical key this node claims, or undefined. Claiming it is not holding it: see keyClaims. */
  readonly keyCode: string | undefined;
  /** The gate stamped on this node, or undefined for a node nothing gates. */
  readonly gate: ControlGate | undefined;
  readonly style: Readonly<Record<string, string>>;
  readonly children: readonly ControlNode[];
}

export interface ControlDocument {
  readonly key: string;
  readonly activityId: string;
  readonly canvasKind: string;
  readonly nodes: readonly ControlNode[];
}

export interface ControlOptions {
  /** The canvas kinds this person reaches, as the welcome named them. Never a hardcoded kind. */
  readonly canvasKinds: readonly string[];
  readonly report?: SceneReport;
}

interface BuildContext {
  readonly entities: EntityIndex;
  readonly report: SceneReport;
}

/** Index entities the way every binding looks them up, so a caller never restates the scope key. */
export function controlEntityIndex(entities: readonly Entity[]): EntityIndex {
  const index = new Map<string, Entity>();
  for (const held of entities) index.set(entityKey(held.scope, held.id), held);
  return index;
}

function barFill(values: BoundValues): number | undefined {
  const bound = values.get(BAR_VALUE_KEY);
  if (bound?.kind !== "number") return undefined;

  const { value, min, max } = bound;
  if (min === undefined || max === undefined) return undefined;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return undefined;

  return Math.min(1, Math.max(0, (value - min) / (max - min)));
}

/** The gate stamped on this node, or undefined for a node nothing gates.
 *
 * Absent is ungated and present-and-false is armed, which is not the same fact: a renderer reading
 * a missing gate as armed would draw a paid control armed the first moment stamping failed, and
 * that is the one direction this may never fail in.
 */
function gateOf(node: SceneNode): ControlGate | undefined {
  const gate = node.gate;
  if (gate === null || gate === undefined) return undefined;
  return { locked: gate.locked, label: gate.label ?? "", mode: gate.mode };
}

/**
 * Whether a locked node is omitted rather than drawn locked.
 *
 * `hide` is safe **only where layout flows**. A hole in absolute layout is itself information: it
 * tells whoever is looking that something they may not use sits exactly there, which on a
 * per-person canvas is one person learning what another person may do. This half flows, so the
 * omission closes up and says nothing. Do not carry this branch into a build that positions nodes
 * absolutely without reflowing what is left.
 */
function hiddenByGate(gate: ControlGate | undefined): boolean {
  return gate !== undefined && gate.locked && gate.mode === GATE_MODE_HIDE;
}

/**
 * What a press on this node sends, or undefined for a node nothing presses.
 *
 * An action inside a `repeat` may omit its value, and the omission means the current item's entity
 * id, so a per-row control needs no per-row authoring. Outside a repeat there is nothing to fall
 * back to, and an action that would send nothing is dropped rather than drawn as a control that
 * cannot work.
 */
function pressOf(
  node: SceneNode,
  item: Entity | undefined,
  report: SceneReport,
): ControlAction | undefined {
  const action = node.action;
  if (action === null || action === undefined) return undefined;

  const value = action.value ?? item?.id;
  if (value === undefined || value === "") {
    report.once(
      `action:${node.id}`,
      `node "${node.id}" has an action with no value and no enclosing repeat to take one from, so it is not pressable`,
    );
    return undefined;
  }

  return { submission: action.submission, value };
}

/** The accessible name this kind owes, which is never empty and never a platform word.
 *
 * `Convention_ASceneNodeOwesAnAccessibleNamePerKind`. The author's own text wins; a repeated
 * control with no text is named by what it submits, which is the item's own id; and a node that
 * supplied neither falls back to its id, so a control is always reachable by name.
 */
function accessibleName(text: string, action: ControlAction | undefined, id: string): string {
  if (text.length > 0) return text;
  if (action !== undefined && action.value.length > 0) return action.value;
  return id;
}

function flowOf(node: SceneNode): ControlFlow | undefined {
  const flow = node.flow;
  if (flow === null || flow === undefined) return undefined;
  return {
    direction: flow.direction ?? DEFAULT_FLOW_DIRECTION,
    gap: flow.gap ?? DEFAULT_FLOW_GAP,
  };
}

interface Instance {
  readonly node: SceneNode;
  readonly item: Entity | undefined;
}

/**
 * One instance per plain node and one per entity a `repeat` node matches.
 *
 * Every parent in this half flows, so a repeat always has somewhere defined to sit and never needs
 * the enclosing-flow check the absolute build has to make.
 */
function instancesOf(
  nodes: readonly SceneNode[],
  context: BuildContext,
  outerItem: Entity | undefined,
): Instance[] {
  const instances: Instance[] = [];

  for (const node of nodes) {
    const repeat = node.repeat;
    if (repeat === null || repeat === undefined) {
      instances.push({ node, item: outerItem });
      continue;
    }
    if (outerItem !== undefined) {
      // A rendered key carries one item id and a nested repeat would need two. The sdk refuses
      // this at construction, so reaching here means a document that skipped validation.
      context.report.once(
        `nested:${node.id}`,
        `node "${node.id}" repeats inside another repeat, which one key cannot name, so it is not drawn`,
      );
      continue;
    }
    for (const item of matchingEntities(repeat, context.entities)) instances.push({ node, item });
  }

  return instances;
}

function buildNodes(
  nodes: readonly SceneNode[],
  prefix: string,
  context: BuildContext,
  outerItem: Entity | undefined,
  maxItems: number,
  ownerId: string,
): readonly ControlNode[] {
  const all = instancesOf(nodes, context, outerItem);
  const visible = all.length > maxItems ? all.slice(0, maxItems) : all;
  if (all.length > maxItems) {
    context.report.once(
      `flow:${ownerId}`,
      `node "${ownerId}" flow holds ${String(all.length)} items, showing ${String(maxItems)}`,
    );
  }

  const built: ControlNode[] = [];
  for (const instance of visible) {
    const child = buildNode(instance.node, prefix, context, instance.item);
    if (child !== undefined) built.push(child);
  }
  return built;
}

function buildNode(
  node: SceneNode,
  prefix: string,
  context: BuildContext,
  item: Entity | undefined,
): ControlNode | undefined {
  if (!isControlKind(node.kind)) {
    context.report.once(
      `kind:${node.kind}`,
      `node kind "${node.kind}" is not drawn by the interact half`,
    );
    return undefined;
  }

  const gate = gateOf(node);
  if (hiddenByGate(gate)) return undefined;

  const bindings = node.bindings ?? [];
  const values = resolveBindings(bindings, context.entities, item);
  if (!predicatesHold(bindings, values)) return undefined;

  const fill = node.kind === "bar" ? barFill(values) : undefined;
  if (node.kind === "bar" && fill === undefined) {
    context.report.once(
      `bar:${node.id}`,
      `node "${node.id}" is a bar with no "${BAR_VALUE_KEY}" binding to a bounded number, so it is not drawn`,
    );
    return undefined;
  }

  const action = node.kind === INPUT_KIND ? pressOf(node, item, context.report) : undefined;
  const text = formatText(node.text, values);
  const key = item === undefined ? `${prefix}/${node.id}` : `${prefix}/${node.id}/${item.id}`;
  const flow = flowOf(node);

  return {
    key,
    id: node.id,
    kind: node.kind,
    text,
    name: accessibleName(text, action, node.id),
    fill,
    flow,
    action,
    keyCode: action === undefined ? undefined : (node.key_code ?? undefined),
    gate,
    style: { ...node.style },
    children: buildNodes(
      node.children ?? [],
      prefix,
      context,
      item,
      node.flow?.max_items ?? DEFAULT_FLOW_MAX_ITEMS,
      node.id,
    ),
  };
}

function buildDocument(
  document: SceneDocument,
  options: ControlOptions,
  report: SceneReport,
  entities: EntityIndex,
): ControlDocument | undefined {
  const version = document.schema_version ?? SUPPORTED_SCHEMA_VERSION;
  if (version !== SUPPORTED_SCHEMA_VERSION) {
    report.once(
      `version:${String(version)}`,
      `scene schema version ${String(version)} is not drawn by this build`,
    );
    return undefined;
  }

  // The kind is asked of the connection, never of a table and never of a constant. A control layer
  // reaches whichever kinds its role resolved to, and an activity may have named any of them.
  if (!options.canvasKinds.includes(document.canvas_kind)) {
    report.once(
      `canvas:${document.canvas_kind}`,
      `document for canvas "${document.canvas_kind}" reached a control layer that holds ${options.canvasKinds.join(", ") || "no canvas"}`,
    );
    return undefined;
  }

  // The instance, falling back to the activity: one package added twice sends two documents.
  const key = `${document.added_activity_id ?? document.activity_id}/${document.canvas_kind}`;
  const context: BuildContext = { entities, report };

  return {
    key,
    activityId: document.activity_id,
    canvasKind: document.canvas_kind,
    nodes: buildNodes(
      document.nodes ?? [],
      key,
      context,
      undefined,
      DEFAULT_FLOW_MAX_ITEMS,
      key,
    ),
  };
}

/** Build every document a control layer holds. A document it cannot draw contributes nothing. */
export function buildControls(
  documents: readonly SceneDocument[],
  entities: EntityIndex,
  options: ControlOptions,
): readonly ControlDocument[] {
  const report = options.report ?? sceneReport;
  const built: ControlDocument[] = [];

  for (const document of documents) {
    const drawn = buildDocument(document, options, report, entities);
    if (drawn !== undefined) built.push(drawn);
  }

  return built;
}

interface Claim {
  readonly node: ControlNode;
  readonly activityId: string;
}

function collectClaims(nodes: readonly ControlNode[], activityId: string, into: Map<string, Claim[]>): void {
  for (const node of nodes) {
    const code = node.keyCode;
    if (code !== undefined) {
      const held = into.get(code);
      if (held === undefined) into.set(code, [{ node, activityId }]);
      else held.push({ node, activityId });
    }
    collectClaims(node.children, activityId, into);
  }
}

/** Which control each physical key reaches. A key two documents claim reaches neither. */
export function keyClaims(
  documents: readonly ControlDocument[],
  report: SceneReport = sceneReport,
): ReadonlyMap<string, ControlNode> {
  const claims = new Map<string, Claim[]>();
  for (const document of documents) collectClaims(document.nodes, document.activityId, claims);

  const held = new Map<string, ControlNode>();
  for (const [code, claimants] of claims) {
    const first = claimants[0];
    if (first === undefined) continue;
    if (claimants.length === 1) {
      held.set(code, first.node);
      continue;
    }
    report.once(
      `contested:${code}`,
      `key "${code}" is claimed by ${claimants.map((one) => one.activityId).join(", ")} on one canvas, so it reaches none of them; press the control instead`,
    );
  }

  return held;
}
